import * as actionTypes from "./actionTypes";
import * as urlList from "../../config";
import axios from "axios";
import { getCookie } from "../../services/cookie";
import { showError } from "./errorActions";
import {getRecoverySearchResults} from "./filterActions";
import {DELETED_STATUS_OPTIONS} from "../../services/constList";
const VIEW_TYPES = { MODULES: 'module', CLUSTERS: 'cluster' };

/**
 * Dispatches an action to restore previously deleted or pending deletion items (Modules or Clusters).
 *
 * @param {Array<string>} itemIdsToRestore - An array of item IDs (strings).
 * @param {string} viewType - The type of items being restored ('module' or 'cluster').
 * @param {object} currentFilters - The filters currently applied to the list, used for refetching.
 * @returns {function} A thunk function that performs the async operation.
 */
export const restoreItems = (itemIdsToRestore, viewType, currentFilters) => {
    return async (dispatch, getState) => {
        dispatch(restoreItemsRequest());


        let url;
        const payloadIdKey = viewType === VIEW_TYPES.MODULES ? 'moduleName' : 'clusterId';

        if (viewType === VIEW_TYPES.MODULES) {
            url = urlList.RESTORE_MODULES_URL;
        } else if (viewType === VIEW_TYPES.CLUSTERS) {
            url = urlList.RESTORE_CLUSTERS_URL;
        } else {
            const errorMsg = `Invalid viewType provided for restoring items: ${viewType}`;
            console.error(errorMsg);
            dispatch(showError(errorMsg));
            dispatch(restoreItemsFailure(errorMsg));
            return;
        }

        if (!url) {
            const errorMsg = `API endpoint for restoring ${viewType}s is not defined in config.`;
            console.error(errorMsg);
            dispatch(showError(errorMsg));
            dispatch(restoreItemsFailure(errorMsg));
            return;
        }

        const payload = itemIdsToRestore.map(id => ({
            [payloadIdKey]: id
        }));

        console.log(`[Restore Action] Attempting to restore ${viewType}s with ID list:`, JSON.parse(JSON.stringify(payload)));

        if (!payload || payload.length === 0) {
            const errorMsg = `No item IDs provided for restoration.`;
            console.warn(errorMsg);
            dispatch(restoreItemsFailure(errorMsg));
            return;
        }

        try {
            const response = await axios({
                url: url,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'xAuthLuxotticaToken': getCookie('xAuthLuxotticaToken'),
                },
                data: JSON.stringify(payload),
            });

            console.log(`${viewType} restore successful:`, response.data);
            dispatch(restoreItemsSuccess(itemIdsToRestore, viewType));


            if (currentFilters) {
                const actualCurrentFilters = getState().filterRecovery.exportSelectedFilters || currentFilters;
                const statusesForRefetch = actualCurrentFilters.status || DELETED_STATUS_OPTIONS.map(opt => opt.value);
                const moduleTypeForRefetch = actualCurrentFilters.moduleType || null;
                const pagination = getState().filterRecovery.exportPagination;
                const pageNumberForRefetch = pagination?.currentPage ?? 0;
                const pageSizeForRefetch = pagination?.pageSize ?? 50;


                console.log(`Refetching ${viewType} list with filters after restore.`);
                dispatch(getRecoverySearchResults(
                    viewType,
                    actualCurrentFilters,
                    statusesForRefetch,
                    moduleTypeForRefetch,
                    pageNumberForRefetch,
                    pageSizeForRefetch
                ));
            } else {
                console.warn("Current filters not provided, cannot automatically refresh list after restore.");
            }

        } catch (error) {
            console.error(`Error restoring ${viewType}s:`, error);
            const errorMsg = error.response?.data?.message || error.message || `Failed to restore ${viewType}s.`;
            dispatch(showError(errorMsg));
            dispatch(restoreItemsFailure(errorMsg));
        }
    };
};

/**
 * Action creator for the restore items request start.
 * @returns {object} The action object.
 */
const restoreItemsRequest = () => {
    return {
        type: actionTypes.RESTORE_ITEMS_REQUEST,
    };
};

/**
 * Action creator for successful item restoration.
 * @param {Array<string>} restoredIds - The IDs of the items that were part of the restore request.
 * @param {string} viewType - The type ('module' or 'cluster') of items restored.
 * @returns {object} The action object.
 */
const restoreItemsSuccess = (restoredIds, viewType) => {
    return {
        type: actionTypes.RESTORE_ITEMS_SUCCESS,
        payload: { restoredIds, viewType },
    };
};

/**
 * Action creator for failed item restoration.
 * @param {string} errorMessage - The error message string.
 * @returns {object} The action object.
 */
const restoreItemsFailure = (errorMessage) => {
    return {
        type: actionTypes.RESTORE_ITEMS_FAILURE,
        error: errorMessage,
    };
};